import { useState } from 'react';
import {
  faChevronDown,
  faChevronUp,
  faCircleCheck,
  faCircleXmark,
  faSpinner,
  faXmark,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Progress } from '@/components/ui/progress';
import { ExtraSmallText, SmallText } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import type { UploadingFile } from '../hooks/useUpload';
import { formatFileSize } from '../utils';

interface UploadProgressPanelProps {
  uploadingFiles: UploadingFile[];
  onDismiss: () => void;
  onCancelUpload?: (id: string) => void;
}

export function UploadProgressPanel({
  uploadingFiles,
  onDismiss,
  onCancelUpload,
}: UploadProgressPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  if (uploadingFiles.length === 0) return null;

  const completedCount = uploadingFiles.filter((f) => f.status === 'success').length;
  const errorCount = uploadingFiles.filter((f) => f.status === 'error').length;
  const totalCount = uploadingFiles.length;
  const allDone = completedCount + errorCount === totalCount;
  const isUploading = !allDone;

  const overallProgress = isUploading
    ? Math.round(
        uploadingFiles.reduce((sum, f) => {
          if (f.status === 'success') return sum + 100;
          if (f.status === 'error') return sum + 0;
          return sum + f.progress;
        }, 0) / totalCount,
      )
    : 100;

  const headerText = isUploading
    ? `Uploading ${totalCount} ${totalCount === 1 ? 'File' : 'Files'}`
    : `${completedCount} ${completedCount === 1 ? 'Upload' : 'Uploads'} Completed`;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-[360px] overflow-hidden rounded-lg border border-border bg-background shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <SmallText className="font-medium">{headerText}</SmallText>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            colorScheme="secondary"
            onClick={() => setIsExpanded((v) => !v)}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            <Icon icon={isExpanded ? faChevronDown : faChevronUp} />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            colorScheme="secondary"
            onClick={onDismiss}
            aria-label="Dismiss"
          >
            <Icon icon={faXmark} />
          </Button>
        </div>
      </div>

      {/* Progress bar — shown only during upload */}
      {isUploading && <Progress value={overallProgress} className="h-0.5 rounded-none" />}

      {/* File list */}
      <div
        className={cn(
          'overflow-hidden transition-all duration-200 ease-in-out',
          isExpanded ? 'max-h-[320px]' : 'max-h-0',
        )}
      >
        <div className="max-h-[320px] overflow-y-auto">
          {uploadingFiles.map((uploadingFile) => (
            <div
              key={uploadingFile.id}
              className="flex items-center gap-3 border-t border-border px-4 py-3"
            >
              <SmallText className="min-w-0 flex-1 truncate font-medium leading-tight">
                {uploadingFile.file.name}
              </SmallText>

              <ExtraSmallText className="shrink-0 text-muted-foreground">
                {formatFileSize(uploadingFile.file.size)}
              </ExtraSmallText>

              <div className="flex shrink-0 items-center gap-1">
                {(uploadingFile.status === 'preparing' || uploadingFile.status === 'uploading') && (
                  <>
                    <Icon icon={faSpinner} className="animate-spin text-primary" />
                    {onCancelUpload && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        colorScheme="secondary"
                        onClick={() => onCancelUpload(uploadingFile.id)}
                        aria-label="Cancel upload"
                      >
                        <Icon icon={faXmark} />
                      </Button>
                    )}
                  </>
                )}
                {uploadingFile.status === 'success' && (
                  <Icon icon={faCircleCheck} className="text-green-600" />
                )}
                {uploadingFile.status === 'error' && (
                  <Icon icon={faCircleXmark} className="text-destructive" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
